import { addDays, format, parse, getDay, subDays, isBefore } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from 'react-query'
import { camelize } from 'humps'

import {
  Link,
  ProgrammingTrack,
  ProgrammingClassType,
  CompeteWorkout,
  LiftingWorkout,
  DailyClassPlan,
  AffiliateWorkout,
  ClassPlan,
  AtHomeWorkout,
} from './types'
import { useProgrammingDates } from 'components/Settings'
import { fetchProgrammingContent } from 'services/programming'
import * as commerceService from 'services/commerce'

type BaseClassPlanResponse = {
  programmingClassType: ProgrammingClassType
  date: string
  fullPath: string
  name: string
}

type AtHomeDailyClassPlanResponse = BaseClassPlanResponse & {
  programmingTrack: ProgrammingTrack.AtHomeTrack
  workouts?: AtHomeWorkout[]
  accessoryWork?: {
    description: string
    title: string
  }[]
  videoOverview?: {
    youtubeId: string
  }
}

type CompeteDailyClassPlanResponse = BaseClassPlanResponse & {
  programmingTrack: ProgrammingTrack.CompeteTrack
  workouts?: CompeteWorkout[]
  stretching?: {
    description: string
  }
  warmUp?: {
    description: string
    notes: string
  }
  dailyFocus?: string
  dailyTip?: string
  workoutOrder?: {
    element: string
    timeEstimate: string
  }[]
}

type LiftingDailyClassPlanResponse = BaseClassPlanResponse & {
  programmingTrack: ProgrammingTrack.LiftingTrack
  accessoryWork: { title: string; description: string }[]
  workouts?: LiftingWorkout[]
}

type AffiliateDailyClassPlanResponse = BaseClassPlanResponse & {
  programmingTrack: ProgrammingTrack.AffiliateTrack
  accessoryWork?: { title: string; description: string }[]
  workouts?: AffiliateWorkout[]
  aboutTheWorkout?: {
    coachingGoals: { goal: string }[]
    intendedStimulus: { stimulus: string }[]
    videoOverview: {
      youtubeId: string
      linkText: string
    }
  }
  logistics?: {
    limitations: string
    otherConsiderations: string
  }
  scaling?: {
    movementScalingOptions?: {
      movement: string
      substitutions: string
    }[]
    overview: string
  }
  videoResources?: {
    title: string
    youtubeId: string
  }[]
  classPlan?: ClassPlan[]
}

type DailyClassPlanResponse =
  | AffiliateDailyClassPlanResponse
  | AtHomeDailyClassPlanResponse
  | CompeteDailyClassPlanResponse
  | LiftingDailyClassPlanResponse

type WeeklyOverviewResponse = {
  name: string
  monthlyFocus: {
    bodyText: string
    link: Link
    subheading: string
  }
  atAGlance: {
    overview?: string
    weeklyTip: string
    youtubeId?: string
  }
  workYourWeakness?: {
    body: string
    title: string
  }[]
}

function parseSlugToDate(slug: string) {
  return parse(slug, 'yyyyMMdd', new Date())
}

function formatDate(date: Date) {
  return format(date, 'yyyyMMdd')
}

function getStartOfWeek(date: Date) {
  const dayOfWeek = getDay(date)
  let monday = date

  if (dayOfWeek === 0) {
    monday = subDays(date, 6)
  } else if (dayOfWeek > 1) {
    monday = subDays(date, dayOfWeek - 1)
  }

  return monday
}

function weeklyOverviewSlug(track, date) {
  return `/programming/${track}/weekly-overview/${formatDate(date)}`
}

function dailyClassPlanSlug(track, date) {
  return `/programming/${track}/daily-class-plan/${formatDate(date)}`
}

export function useDailyClassPlan(track: string, slug: string) {
  const { latestProgrammingWeekDate, programmingStartDate } =
    useProgrammingDates()

  const date = parseSlugToDate(slug)
  return useQuery<DailyClassPlanResponse, unknown, DailyClassPlan>(
    ['daily-class-plan', track, slug],
    () =>
      fetchProgrammingContent(`/programming/${track}/daily-class-plan/${slug}`),
    {
      select: (data) => {
        const { name, fullPath, programmingTrack, programmingClassType } = data
        const dayOfWeek = getDay(date)
        const monday = getStartOfWeek(date)

        const baseResult = {
          track: programmingTrack,
          type: programmingClassType,
          slug: fullPath,
          date,
          name,
          slugs: {
            weeklyOverview: weeklyOverviewSlug(track, monday),
            nextDay:
              dayOfWeek !== 0 ||
              Number(latestProgrammingWeekDate) > Number(data.date)
                ? dailyClassPlanSlug(track, addDays(date, 1))
                : undefined,
            previousDay:
              dayOfWeek !== 1 ||
              Number(programmingStartDate) < Number(data.date)
                ? dailyClassPlanSlug(track, subDays(date, 1))
                : undefined,
            affiliateTrack: dailyClassPlanSlug('affiliate', date),
            atHomeTrack: dailyClassPlanSlug('at-home', date),
            competeTrack: dailyClassPlanSlug('compete', date),
            liftingTrack: dailyClassPlanSlug('lifting', date),
          },
        }

        switch (programmingTrack) {
          case ProgrammingTrack.AtHomeTrack: {
            const { accessoryWork, videoOverview, workouts } = data

            return {
              ...baseResult,
              track: ProgrammingTrack.AtHomeTrack,
              accessoryWork,
              videoOverview,
              workouts,
            }
          }
          case ProgrammingTrack.CompeteTrack: {
            const {
              dailyFocus,
              dailyTip,
              workoutOrder,
              stretching,
              warmUp,
              workouts,
            } = data
            return {
              ...baseResult,
              track: ProgrammingTrack.CompeteTrack,
              dailyFocus,
              dailyTip,
              workoutOrder,
              stretching,
              warmUp,
              workouts,
            }
          }
          case ProgrammingTrack.LiftingTrack: {
            const { accessoryWork, workouts } = data

            return {
              ...baseResult,
              track: ProgrammingTrack.LiftingTrack,
              accessoryWork,
              workouts,
            }
          }
          case ProgrammingTrack.AffiliateTrack: {
            const {
              aboutTheWorkout,
              accessoryWork,
              videoResources,
              workouts,
              classPlan,
              logistics,
              scaling,
            } = data

            let hourTime = 0

            return {
              ...baseResult,
              track: ProgrammingTrack.AffiliateTrack,
              aboutTheWorkout,
              accessoryWork,
              workouts,
              logistics,
              scaling,
              videoResources,
              classPlan: classPlan?.map((plan) => {
                const duration = Number(plan.duration)
                hourTime += duration
                return { ...plan, title: camelize(plan.title), hourTime }
              }),
            }
          }
        }
      },
    },
  )
}
//
export function useDateSlugs() {
  const {
    latestProgrammingWeekDate,
    programmingStartDate,
    programmingActivationDate,
  } = useProgrammingDates()

  const minimumDateSlug = useMemo(() => {
    if (programmingActivationDate) {
      // Gives them access to access to at least the week they signup and prior
      return formatDate(getStartOfWeek(subDays(programmingActivationDate, 7)))
    }

    return programmingStartDate
  }, [programmingStartDate, programmingActivationDate])

  const currentWeekSlug = useMemo(() => {
    const now = Date.now()
    const max = parseSlugToDate(latestProgrammingWeekDate)

    if (isBefore(now, max)) {
      return formatDate(subDays(max, 7))
    }

    return latestProgrammingWeekDate
  }, [latestProgrammingWeekDate])

  return {
    minimumDateSlug,
    currentWeekSlug,
    maximumDateSlug: latestProgrammingWeekDate,
  }
}

export function useWeeklyOverview(track: string, slug?: string) {
  const { minimumDateSlug, currentWeekSlug, maximumDateSlug } = useDateSlugs()
  const dateSlug = slug || currentWeekSlug
  const date = parseSlugToDate(dateSlug)

  const overviewQuery = useQuery(
    ['weekly-overview', track, dateSlug],
    () =>
      fetchProgrammingContent<WeeklyOverviewResponse>(
        `/programming/${track}/weekly-overview/${dateSlug}`,
      ),
    {
      select: (data) => {
        const { monthlyFocus, workYourWeakness, atAGlance, name } = data

        return {
          monthlyFocus,
          workYourWeakness,
          atAGlance,
          date,
          name,
          slugs: {
            previousWeek:
              minimumDateSlug !== dateSlug
                ? weeklyOverviewSlug(track, subDays(date, 7))
                : undefined,
            nextWeek:
              maximumDateSlug !== dateSlug
                ? weeklyOverviewSlug(track, addDays(date, 7))
                : undefined,
            affiliateTrack: weeklyOverviewSlug('affiliate', date),
            atHomeTrack: weeklyOverviewSlug('at-home', date),
            competeTrack: weeklyOverviewSlug('compete', date),
            liftingTrack: weeklyOverviewSlug('lifting', date),
          },
        }
      },
    },
  )

  const mondaySlug = formatDate(date)
  const tuesdaySlug = formatDate(addDays(date, 1))
  const wednesdaySlug = formatDate(addDays(date, 2))
  const thursdaySlug = formatDate(addDays(date, 3))
  const fridaySlug = formatDate(addDays(date, 4))
  const saturdaySlug = formatDate(addDays(date, 5))
  const sundaySlug = formatDate(addDays(date, 6))

  const mondayQuery = useDailyClassPlan(track, mondaySlug)
  const tuesdayQuery = useDailyClassPlan(track, tuesdaySlug)
  const wednesdayQuery = useDailyClassPlan(track, wednesdaySlug)
  const thursdayQuery = useDailyClassPlan(track, thursdaySlug)
  const fridayQuery = useDailyClassPlan(track, fridaySlug)
  const saturdayQuery = useDailyClassPlan(track, saturdaySlug)
  const sundayQuery = useDailyClassPlan(track, sundaySlug)

  const dailyClassPlans = useMemo(() => {
    return [
      mondayQuery.data,
      tuesdayQuery.data,
      wednesdayQuery.data,
      thursdayQuery.data,
      fridayQuery.data,
      saturdayQuery.data,
      sundayQuery.data,
    ]
  }, [
    mondayQuery,
    tuesdayQuery,
    wednesdayQuery,
    thursdayQuery,
    fridayQuery,
    saturdayQuery,
    sundayQuery,
  ])

  const isLoading = useMemo(() => {
    return (
      overviewQuery.isLoading ||
      mondayQuery.isLoading ||
      tuesdayQuery.isLoading ||
      wednesdayQuery.isLoading ||
      thursdayQuery.isLoading ||
      fridayQuery.isLoading ||
      saturdayQuery.isLoading ||
      sundayQuery.isLoading
    )
  }, [
    overviewQuery,
    mondayQuery,
    tuesdayQuery,
    wednesdayQuery,
    thursdayQuery,
    fridayQuery,
    saturdayQuery,
    sundayQuery,
  ])

  return {
    isLoading,
    dailyClassPlans,
    weeklyOverview: overviewQuery.data,
  }
}

export function useStripePaymentPortal() {
  const query = useMutation(
    'billing-portal-url',
    commerceService.fetchBillingPortalURL,
  )

  useEffect(() => {
    if (query.data) {
      window.location.href = query.data
    }
  }, [query.data])

  return [query.mutateAsync, query.isLoading || query.data]
}

export function useStripeCheckoutPortal() {
  const query = useMutation(
    'checkout-portal-url',
    commerceService.fetchCheckoutPortalURL,
  )

  useEffect(() => {
    if (query.data) {
      window.location.href = query.data
    }
  }, [query.data])

  return [query.mutateAsync, query.isLoading || query.data]
}
